import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subscription } from 'rxjs';
import { User } from 'src/app/models/User';

import {
  CLIENT_DOMAIN_STATUS,
  CLIENT_DOMAIN_HOLDING_STATUSES,
  ClientDomain,
  ClientDomainAvailability,
  ClientDomainInstructions,
  ClientDomainPricing,
} from './../../models/ClientDomain';
import { ConfirmationService } from './../../shared/components/confirmation-modal/confirmation.service';
import { ToastService } from './../../shared/components/toast/toast.service';
import { AuthService } from './../../shared/services/auth.service';
import { ClientDomainService } from './../../shared/services/client-domain.service';
import { RechargesService } from './../../shared/services/recharges.service';
import { ADMINISTRATOR, CUSTOMER_SUCCESS, UserService } from './../../shared/services/user.service';

@Component({
  selector: 'app-dominio-proprio',
  templateUrl: './dominio-proprio.component.html',
  styleUrls: ['./dominio-proprio.component.scss'],
})
export class DominioProprioComponent implements OnInit, OnDestroy {
  STATUS = CLIENT_DOMAIN_STATUS;

  user: User;
  // sem assinatura E sem liberação: quem decide é a API (403), não o plano —
  // o cliente grande liberado pelo admin NÃO tem plano e mesmo assim gerencia
  // o domínio (EnsureActivePlan deixa passar quem já tem domínio em andamento)
  blocked = false;

  loading = true;

  /**
   * Ação em andamento, pelo nome do método que a disparou. O botão dela gira
   * (`appLoadingButton`) e `working` trava os demais — sem o overlay global,
   * que saiu no CU-86akkubzz, é o único sinal de que o clique foi ouvido.
   */
  busyAction: string | null = null;

  get working(): boolean {
    return this.busyAction !== null;
  }

  isBusy(action: string): boolean {
    return this.busyAction === action;
  }

  domain: ClientDomain | null = null;
  instructions: ClientDomainInstructions | null = null;

  // formulário de contratação
  domainType: 'byod' | 'purchase' = 'byod';
  domainName = '';
  availability: ClientDomainAvailability | null = null;
  availabilityChecked = false;

  // checkout / renovação
  paymentType = 'pix';
  paymentForm: UntypedFormGroup;
  pixImage: string;
  pixCode: string;
  renewalPricing: ClientDomainPricing | null = null;
  renewing = false;
  transferPrice: number | null = null;
  transferring = false;

  // modo admin: listagem geral + criação em nome de cliente
  adminDomains: ClientDomain[] = [];
  adminPage = 1;
  adminPerPage = 25;
  adminTotal = 0;
  adminCreating = false;
  userControl = new FormControl<User | null>(null);
  allowWithoutPlan = false;
  filteredUsers$: Observable<User[]> | null = null;
  private filterTimer: any;

  subscriptions: Subscription[] = [];

  @ViewChild('pixModal') pixModal: TemplateRef<any>;

  constructor(
    private fb: UntypedFormBuilder,
    private modalService: NgbModal,
    private authService: AuthService,
    private toastService: ToastService,
    private confirmationService: ConfirmationService,
    private clientDomainService: ClientDomainService,
    private rechargesService: RechargesService,
    private userService: UserService
  ) { }

  get isAdmin(): boolean {
    return [ADMINISTRATOR, CUSTOMER_SUCCESS].includes(this.user?.profile_id);
  }

  ngOnInit(): void {
    this.user = this.authService.getUser();
    this.blocked = false;

    this.paymentForm = this.fb.group({
      payment_type: ['pix', Validators.required],
      name: [this.user.name, Validators.required],
      card_number: [''],
      due_date: [''],
      cvv: [''],
    });

    this.subscriptions.push(
      this.authService.userChanged.subscribe(() => {
        this.ngOnDestroy();
        this.ngOnInit();
      })
    );

    if (this.isAdmin) {
      this.loadAdminList();
    } else {
      this.loadDomain();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.subscriptions = [];
  }

  // ---------- modo admin ----------

  loadAdminList(): void {
    this.loading = true;

    this.clientDomainService.adminList(this.adminPage, this.adminPerPage).subscribe({
      next: result => {
        this.adminDomains = result?.data || [];
        this.adminTotal = result?.total || 0;
        this.adminPage = result?.current_page || 1;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.toastService.error('Erro ao carregar os domínios.');
      },
    });
  }

  adminPageChanged(page: number): void {
    this.adminPage = page;
    this.loadAdminList();
  }

  selectDomain(domain: ClientDomain): void {
    this.loading = true;

    this.clientDomainService.getById(domain.id).subscribe({
      next: res => {
        // o show não carrega a relação user — preserva o dono vindo da listagem
        this.domain = { ...res.domain, user: res.domain.user || domain.user };
        this.instructions = res.instructions;
        this.renewalPricing = res.renewal_pricing || null;
        this.transferPrice = res.transfer_price || null;
        this.loading = false;
      },
      error: () => {
        this.domain = domain;
        this.loading = false;
      },
    });
  }

  backToList(): void {
    this.domain = null;
    this.instructions = null;
    this.adminCreating = false;
    this.availability = null;
    this.availabilityChecked = false;
    this.domainName = '';
    this.userControl.patchValue(null);
    this.loadAdminList();
  }

  startAdminCreate(): void {
    this.adminCreating = true;
    this.availability = null;
    this.availabilityChecked = false;
    this.domainName = '';
    this.userControl.patchValue(null);
    this.filteredUsers$ = null;
  }

  filterUsers(event: any): void {
    if (this.filterTimer) {
      clearTimeout(this.filterTimer);
    }

    const value = (event.target.value || '').trim().toLowerCase();
    const selected = this.userControl.value;

    if (selected && value.length < this.displayUser(selected).length) {
      this.userControl.patchValue(null);
      this.filteredUsers$ = null;
      return;
    }

    if (value.length >= 3) {
      this.filterTimer = setTimeout(() => {
        this.filteredUsers$ = this.userService.filter(value);
      }, 200);
    }
  }

  displayUser(user?: User): string {
    return user ? `${user.name} (${user.email})` : '';
  }

  // ---------- carregamento ----------

  loadDomain(): void {
    if (this.isAdmin) {
      if (this.domain) {
        this.selectDomain(this.domain);
      } else {
        this.loadAdminList();
      }
      return;
    }

    this.loading = true;

    this.clientDomainService.list().subscribe({
      next: domains => {
        const current = (domains || []).find(d => CLIENT_DOMAIN_HOLDING_STATUSES.includes(d.status));

        if (!current) {
          this.domain = null;
          this.instructions = null;
          this.loading = false;
          return;
        }

        this.clientDomainService.getById(current.id).subscribe({
          next: res => {
            this.domain = res.domain;
            this.instructions = res.instructions;
            this.renewalPricing = res.renewal_pricing || null;
            this.transferPrice = res.transfer_price || null;
            this.loading = false;
          },
          error: () => {
            this.domain = current;
            this.loading = false;
          },
        });
      },
      error: res => {
        this.loading = false;

        if (res.status === 403) {
          this.blocked = true;
          return;
        }

        this.toastService.error('Erro ao carregar o domínio próprio.');
      },
    });
  }

  // ---------- contratação ----------

  setDomainType(type: 'byod' | 'purchase'): void {
    this.domainType = type;
    this.availability = null;
    this.availabilityChecked = false;
  }

  domainNameChanged(): void {
    this.availability = null;
    this.availabilityChecked = false;
  }

  checkAvailability(): void {
    if (!this.domainName.trim()) {
      return;
    }

    this.busyAction = 'checkAvailability';
    this.clientDomainService.availability(this.domainName.trim(), this.domainType).subscribe({
      next: res => {
        this.availability = res;
        this.availabilityChecked = true;
        this.busyAction = null;
      },
      error: () => {
        this.busyAction = null;
        this.toastService.error('Erro ao consultar disponibilidade.');
      },
    });
  }

  availabilityReasonText(): string {
    switch (this.availability?.reason) {
      case 'invalid': return 'O domínio informado não é válido.';
      case 'collision_own': return 'Você já tem uma contratação em andamento para este domínio. Continue por ela, ou cancele antes de contratar de novo.';
      case 'collision': return 'Este domínio não está disponível.';
      case 'registrar_unavailable': return 'Este domínio já está registrado e não pode ser comprado pela plataforma. Escolha outro nome — ou, se ele já é seu, contrate pela opção "já tenho um domínio".';
      case 'registrar_unavailable_ours': return 'Este domínio já está registrado na conta da Otimiza no registrador (não está livre para nova compra). Se ele deveria estar disponível, verifique a conta do registrador antes de seguir.';
      case 'tld_not_supported': return 'A extensão deste domínio não é suportada no momento.';
      case 'premium_not_supported': return 'Este é um domínio premium (custo de registro acima do normal) e não está disponível para compra pela plataforma — fale com o suporte.';
      default: return 'Domínio indisponível.';
    }
  }

  registerDomain(): void {
    if (!this.availability?.available) {
      return;
    }

    const total = this.availability.pricing?.total?.toFixed(2).replace('.', ',');

    this.confirmationService.show(
      `Confirma a contratação do domínio ${this.domainName.trim()} por R$ ${total} (pagamento único)?`,
      () => {
        this.busyAction = 'registerDomain';
        const targetUserId = this.isAdmin ? this.userControl.value?.id : undefined;
        const allowWithoutPlan = this.isAdmin && !!targetUserId && this.allowWithoutPlan;

        this.clientDomainService.create(this.domainName.trim(), this.domainType, targetUserId, allowWithoutPlan).subscribe({
          next: res => {
            this.domain = res.domain;
            this.instructions = res.instructions;
            this.adminCreating = false;
            this.busyAction = null;
            this.toastService.success('Domínio cadastrado! Siga as instruções para continuar.');
          },
          error: res => {
            this.busyAction = null;

            if (res.status !== 422) {
              this.toastService.error('Erro ao cadastrar o domínio.');
            }
          },
        });
      }
    );
  }

  // ---------- posse (TXT) ----------

  verifyOwnership(): void {
    this.busyAction = 'verifyOwnership';
    this.clientDomainService.verifyOwnership(this.domain.id).subscribe({
      next: () => {
        this.busyAction = null;
        this.toastService.success('Posse do domínio verificada!');
        this.loadDomain();
      },
      error: res => {
        this.busyAction = null;
        this.toastHttpError(
          res,
          'Erro ao verificar a posse do domínio.',
          'Muitas verificações em sequência — aguarde um minuto antes de tentar de novo. A propagação do registro TXT pode levar alguns minutos.'
        );
      },
    });
  }

  /**
   * 422 já é toastado em vermelho pelo HttpErrorInterceptor global;
   * 429 (throttle) ganha mensagem amigável; resto cai no fallback.
   */
  private toastHttpError(res: any, fallback: string, tooManyMessage: string): void {
    if (res.status === 429) {
      this.toastService.warning(tooManyMessage, 8000);
      return;
    }

    if (res.status !== 422) {
      this.toastService.error(fallback);
    }
  }

  // ---------- pagamento ----------

  togglePaymentType(paymentType: string): void {
    this.paymentType = paymentType;
    this.paymentForm.patchValue({ payment_type: paymentType });
  }

  formatDueDate(): void {
    const dueDate = this.paymentForm.get('due_date')?.value.match(/\d+/g)?.join([]);

    if (!dueDate) {
      this.paymentForm.patchValue({ due_date: '' });
      return;
    }

    let formatted = '';
    for (const digit of dueDate) {
      formatted += digit;
      if (formatted.length === 2) {
        formatted += '/';
      }
    }

    this.paymentForm.patchValue({ due_date: formatted });
  }

  isPaymentValid(): boolean {
    if (this.paymentType === 'pix') {
      return true;
    }

    const form = this.paymentForm.getRawValue();

    return !!(
      form.name?.trim() &&
      form.card_number?.trim() &&
      form.due_date?.trim().length === 7 &&
      +form.cvv
    );
  }

  get isRenewMode(): boolean {
    return this.domain && (
      this.domain.status === this.STATUS.EXPIRED ||
      (this.domain.status === this.STATUS.ACTIVE && this.renewing)
    );
  }

  chargeTotal(): number {
    if (this.transferring && this.transferPrice) {
      return this.transferPrice;
    }

    if (this.isRenewMode && this.renewalPricing) {
      return this.renewalPricing.total;
    }

    return this.domain?.price_total || 0;
  }

  startRenewal(): void {
    this.renewing = true;
    this.transferring = false;
  }

  cancelRenewal(): void {
    this.renewing = false;
  }

  get canRequestTransfer(): boolean {
    return this.domain
      && this.domain.type === 'purchase'
      && !!this.domain.registrar_order_id
      && !this.domain.transfer_paid_at
      && this.domain.status !== this.STATUS.TRANSFERRED
      && !!this.transferPrice;
  }

  startTransfer(): void {
    this.transferring = true;
    this.renewing = false;
  }

  cancelTransfer(): void {
    this.transferring = false;
  }

  checkout(): void {
    if (!this.isPaymentValid()) {
      return;
    }

    const total = this.chargeTotal().toFixed(2).replace('.', ',');

    let message = `Confirma o pagamento de R$ ${total} do add-on de domínio próprio?`;

    if (this.transferring) {
      message = `Confirma a <b>transferência de titularidade</b> do domínio <b>${this.domain.domain}</b> por R$ ${total}? ` +
        'O domínio passa a ser seu no registrador e <b>deixa de funcionar como encurtador na plataforma</b> — ' +
        'os links curtos já enviados dependerão de para onde você apontar o domínio. Essa ação não pode ser desfeita.';
    } else if (this.isRenewMode) {
      message = `Confirma a renovação da assinatura do domínio por R$ ${total} (mais 12 meses${this.domain.type === 'purchase' ? ', incluindo o registro do domínio' : ''})?`;
    }

    this.confirmationService.show(message, () => this.registerCheckout());
  }

  private registerCheckout(): void {
    const payment = this.paymentForm.getRawValue();
    payment.payment_type = this.paymentType;

    if (this.paymentType === 'pix') {
      delete payment.card_number;
      delete payment.due_date;
      delete payment.cvv;
    } else {
      payment.card_number = (payment.card_number || '').replace(/\D/g, '');
    }

    const request$ = this.transferring
      ? this.clientDomainService.requestTransfer(this.domain.id, payment)
      : this.isRenewMode
        ? this.clientDomainService.renew(this.domain.id, payment)
        : this.clientDomainService.checkout(this.domain.id, payment);

    this.busyAction = 'registerCheckout';
    request$.subscribe({
      next: res => {
        this.busyAction = null;
        this.domain = res.domain;
        this.renewing = false;
        this.transferring = false;

        if (this.paymentType === 'pix') {
          this.pixImage = res.recharge?.url_qrcode_pix;
          this.pixCode = res.recharge?.pix_code;
          this.modalService.open(this.pixModal, { size: 'lg' });
          this.toastService.success('Pedido criado! Pague o PIX para liberar o próximo passo.');
        } else {
          this.toastService.success('Pagamento aprovado!');
        }

        this.loadDomain();
      },
      error: res => {
        this.busyAction = null;

        if (res.status !== 422) {
          this.toastService.error('Erro ao efetuar pagamento! Tente novamente mais tarde.');
        }
      },
    });
  }

  // ---------- ações de admin ----------

  adminApprovePayment(): void {
    this.confirmationService.show(
      `Aprovar o pagamento do add-on Domínio Próprio (${this.domain.domain})? O provisionamento inicia automaticamente.`,
      () => {
        this.busyAction = 'adminApprovePayment';
        this.rechargesService.confirm(this.domain.user_recharge_id).subscribe({
          next: () => {
            this.busyAction = null;
            this.toastService.success('Pagamento aprovado!');
            this.loadDomain();
          },
          error: res => {
            this.busyAction = null;

            if (res.status !== 422) {
              this.toastService.error('Erro ao aprovar o pagamento.');
            }
          },
        });
      }
    );
  }

  adminConfirmTransfer(): void {
    this.confirmationService.show(
      `Confirmar que a transferência de <b>${this.domain.domain}</b> foi concluída no registrador? O lado da plataforma será desfeito (shortener removido, cliente volta pro encurtador compartilhado).`,
      () => {
        this.busyAction = 'adminConfirmTransfer';
        this.clientDomainService.confirmTransfer(this.domain.id).subscribe({
          next: () => {
            this.busyAction = null;
            this.toastService.success('Transferência concluída!');
            this.backToList();
          },
          error: res => {
            this.busyAction = null;

            if (res.status !== 422) {
              this.toastService.error('Erro ao confirmar a transferência.');
            }
          },
        });
      }
    );
  }

  adminConfirmInfra(): void {
    this.confirmationService.show(
      `Confirmar que a infra de ${this.domain.domain} foi concluída no servidor (HOSTS + certificado)? O domínio será ativado.`,
      () => {
        this.busyAction = 'adminConfirmInfra';
        this.clientDomainService.confirmInfra(this.domain.id).subscribe({
          next: () => {
            this.busyAction = null;
            this.toastService.success('Domínio ativado!');
            this.loadDomain();
          },
          error: res => {
            this.busyAction = null;

            if (res.status !== 422) {
              this.toastService.error('Erro ao confirmar a infra.');
            }
          },
        });
      }
    );
  }

  adminRemoveDomain(): void {
    this.confirmationService.show(
      `Remover DEFINITIVAMENTE o domínio <b>${this.domain.domain}</b>? O cliente volta para o encurtador compartilhado e os links já enviados nesse domínio <b>deixarão de funcionar</b> quando ele for removido do servidor. Essa ação não pode ser desfeita.`,
      () => {
        this.busyAction = 'adminRemoveDomain';
        this.clientDomainService.adminCancel(this.domain.id).subscribe({
          next: () => {
            this.busyAction = null;
            this.toastService.success('Domínio removido. O ops foi notificado para tirar o host do servidor.');
            this.backToList();
          },
          error: res => {
            this.busyAction = null;

            if (res.status !== 422) {
              this.toastService.error('Erro ao remover o domínio.');
            }
          },
        });
      }
    );
  }

  // ---------- DNS ----------

  verifyDns(): void {
    this.busyAction = 'verifyDns';
    this.clientDomainService.verifyDns(this.domain.id).subscribe({
      next: domain => {
        this.busyAction = null;

        if (domain.status === this.STATUS.PROVISIONING_INFRA) {
          this.toastService.success('Apontamento verificado! Estamos ativando seu domínio.');
          this.loadDomain();
          return;
        }

        this.domain = domain;

        if (domain.cloudflare_proxied) {
          this.toastService.warning(
            'Seu domínio parece estar atrás do proxy da Cloudflare (nuvem laranja). Deixe o registro em modo "DNS only".'
          );
          return;
        }

        this.toastService.warning('Apontamento ainda não encontrado. A propagação pode levar algumas horas.');
      },
      error: res => {
        this.busyAction = null;
        this.toastHttpError(
          res,
          'Erro ao verificar o apontamento.',
          'Muitas verificações em sequência — aguarde um minuto antes de tentar de novo. A propagação de DNS pode levar algumas horas, e nós re-checamos automaticamente a cada 15 minutos.'
        );
      },
    });
  }

  // ---------- utilitários ----------

  canCancel(): boolean {
    return this.domain && ![
      this.STATUS.ACTIVE,
      this.STATUS.SUSPENDED,
      this.STATUS.CANCELLED,
    ].includes(this.domain.status);
  }

  cancelDomain(): void {
    this.confirmationService.show(
      `Cancelar a contratação do domínio ${this.domain.domain}?`,
      () => {
        this.busyAction = 'cancelDomain';
        this.clientDomainService.cancel(this.domain.id).subscribe({
          next: () => {
            this.busyAction = null;
            this.toastService.success('Contratação cancelada.');
            this.domain = null;
            this.instructions = null;
            this.availability = null;
            this.availabilityChecked = false;
            this.domainName = '';

            if (this.isAdmin) {
              this.loadAdminList();
            }
          },
          error: res => {
            this.busyAction = null;

            if (res.status !== 422) {
              this.toastService.error('Erro ao cancelar.');
            }
          },
        });
      }
    );
  }

  copy(value: string, message: string = 'Copiado para a área de transferência!'): void {
    navigator.clipboard.writeText(value);
    this.toastService.success(message);
  }

  statusLabel(domain: ClientDomain = this.domain): string {
    switch (domain?.status) {
      case this.STATUS.PENDING_OWNERSHIP: return 'Aguardando verificação de posse';
      case this.STATUS.PENDING_PAYMENT: return 'Aguardando pagamento';
      case this.STATUS.REGISTERING: return 'Registrando domínio';
      case this.STATUS.PENDING_DNS: return 'Aguardando apontamento de DNS';
      case this.STATUS.PROVISIONING_INFRA: return 'Ativação em andamento';
      case this.STATUS.ACTIVE: return 'Ativo';
      case this.STATUS.EXPIRED: return 'Assinatura vencida';
      case this.STATUS.TRANSFERRED: return 'Transferido';
      case this.STATUS.SUSPENDED: return 'Suspenso';
      case this.STATUS.FAILED: return 'Falhou';
      case this.STATUS.CANCELLED: return 'Cancelado';
      default: return domain?.status || '';
    }
  }
}
